/**
 * SuperAdmin Centre Insights - "Concerns raised" card links, Concerns section and concern panel. READ-ONLY.
 * GET /api/superadmin/centre-insights/concerns  and  /concerns/:id
 * The section loads only while it is mounted: one call per open, chip, page or period change. The panel loads one concern
 * per open. Stale responses are ignored through a cleanup flag. The chip numbers come from meta.counts, so they equal the
 * card. The shared styles and helpers (S, day, errText, Line, LinkLine, Card) come from CentreInsights.js via the ui prop.
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import api from '../../utils/api';

const BASE = '/superadmin/centre-insights';
const STATE_CHIPS = [['all', 'All raised', 'raised'], ['open', 'Open', 'open'], ['resolved', 'Resolved', 'resolved']];
const words = v => String(v || '-').replace(/_/g, ' ');

/** Concerns card. Plain lines unless onOpen is given; with it, every line opens the Concerns section. */
export function ConcernsCard({ ui, concerns, solo, excludesSolo, notYet, onOpen }) {
  const { Card, Line, LinkLine } = ui;
  return (
    <Card title="Concerns raised">
      {solo ? <div style={ui.S.note}>{notYet}</div> : concerns ? <>
        {[['Raised', 'all', concerns.raised], ['Open', 'open', concerns.open], ['Resolved', 'resolved', concerns.resolved]].map(([label, state, n]) => (onOpen
          ? <LinkLine key={label} label={label} what="concerns" value={n} onClick={() => onOpen(state)} />
          : <Line key={label} label={label} value={n} />))}
        {excludesSolo && <div style={ui.S.note}>Excludes solo healers</div>}</> : <div style={ui.S.note}>Unavailable right now</div>}
    </Card>
  );
}

export function ConcernsSection({ ui, tenantId, period, initialState, onClose, onOpenConcern }) {
  const { S, day, errText } = ui;
  const [state, setState] = useState(initialState);
  const [paging, setPaging] = useState({ scope: '', n: 1 });
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState({ loading: true, error: '', rows: [], meta: null });
  const [counts, setCounts] = useState({ key: '', value: null });

  // The period applies to every chip. The page goes back to 1 whenever the scope (centre, chip or period) changes.
  const scope = `${tenantId}|${state}|${period}`;
  const page = paging.scope === scope ? paging.n : 1;

  useEffect(() => {
    let current = true; // false after cleanup, so a stale or unmounted response is ignored
    setData({ loading: true, error: '', rows: [], meta: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/concerns`, { params: { tenantId, period, state, page } });
        if (!current) return;
        setData({ loading: false, error: '', rows: res.data.data, meta: res.data.meta });
        setCounts({ key: `${tenantId}|${period}`, value: res.data.meta.counts });
      } catch (e) { if (current) setData({ loading: false, error: errText(e), rows: [], meta: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, period, state, page, attempt, errText]);

  const { loading, error, rows, meta } = data;
  const shownCounts = counts.key === `${tenantId}|${period}` ? counts.value : null;
  const goTo = n => setPaging({ scope, n });
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '16px', margin: 0 }}>Concerns</h3>
        <button type="button" style={S.btn} onClick={onClose}>Close</button>
      </div>
      <div style={S.row}>
        {STATE_CHIPS.map(([v, l, k]) => (
          <button key={v} type="button" style={S.chip(state === v)} onClick={() => setState(v)}>
            {l}{shownCounts ? ` (${shownCounts[k]})` : ''}</button>))}
      </div>
      {meta && <div style={{ ...S.row, fontSize: '14px', fontWeight: 600 }}>
        Total: {meta.total} {meta.total === 1 ? 'concern' : 'concerns'}</div>}
      {error && <div style={S.error}>{error} <button type="button" style={S.btn} onClick={() => setAttempt(a => a + 1)}>Retry</button></div>}
      {loading && <p>Loading...</p>}
      {!loading && !error && rows.length === 0 && <p style={S.note}>No concerns for this filter</p>}
      {rows.length > 0 && <div style={S.scroll}><table style={{ ...S.table, minWidth: '720px' }}>
        <thead><tr>{['Raised on', 'Patient', 'Healer', 'Session', 'State', 'Hold'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id}>
            <td style={S.td}>{day(r.raisedAt)}</td>
            <td style={S.td}><button type="button" style={S.nameBtn} onClick={() => onOpenConcern(r.id)}>{r.patientName}</button></td>
            <td style={S.td}>{r.healerName || '-'}</td>
            <td style={S.td}>{r.sessionNumber}</td>
            <td style={S.td}>{r.state === 'open' ? 'Open' : 'Resolved'}</td>
            <td style={S.td}>{r.adminHold ? 'Admin hold' : ''}</td>
          </tr>))}</tbody>
      </table></div>}
      <div style={S.note}>Opening this list is recorded in the audit log.</div>
      <div style={S.note}>Resolved means the payout line was released, paid or cancelled.</div>
      <div style={S.note}>Only concerns raised by patients are listed. Lines held only by an admin are not included.</div>
      {meta && meta.totalPages > 1 && <div style={{ ...S.row, marginTop: '10px' }}>
        <button type="button" style={S.btn} disabled={page <= 1} onClick={() => goTo(page - 1)}>Previous</button>
        <span style={{ fontSize: '14px' }}>Page {meta.page} of {meta.totalPages}</span>
        <button type="button" style={S.btn} disabled={page >= meta.totalPages} onClick={() => goTo(page + 1)}>Next</button>
      </div>}
    </div>
  );
}

export function ConcernPanel({ ui, tenantId, concernId, onClose, onViewRequest }) {
  const { S, day, errText, Line } = ui;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let current = true; // false after cleanup, so a stale or unmounted response is ignored
    setState({ loading: true, error: '', data: null });
    (async () => {
      try {
        const res = await api.get(`${BASE}/concerns/${concernId}`, { params: { tenantId } });
        if (current) setState({ loading: false, error: '', data: res.data.data });
      } catch (e) { if (current) setState({ loading: false, error: errText(e), data: null }); }
    })();
    return () => { current = false; };
  }, [tenantId, concernId, attempt, errText]);

  const d = state.data;
  return (
    <div style={S.panel} role="dialog" aria-label="Concern details">
      <div style={{ ...S.row, justifyContent: 'space-between' }}>
        <strong style={{ fontSize: '16px' }}>Concern</strong>
        <button type="button" style={S.btn} onClick={onClose} aria-label="Close"><FiX /></button>
      </div>
      <div style={S.note}>Opening this is recorded in the audit log.</div>
      {state.loading && <p>Loading...</p>}
      {state.error && <div style={S.error}>{state.error} <button type="button" style={S.btn} onClick={() => setAttempt(a => a + 1)}>Retry</button></div>}
      {d && <div style={{ marginTop: '12px' }}>
        <Line label="State" value={d.state === 'open' ? 'Open' : 'Resolved (closed: released, paid or cancelled)'} />
        <Line label="Raised on" value={day(d.raisedAt)} />
        {d.state === 'resolved' && d.closedAt && <Line label="Closed on" value={day(d.closedAt)} />}
        <Line label="Session" value={d.sessionNumber} />
        <Line label="Patient" value={d.patientName} />
        <Line label="Healer" value={d.healerName || '-'} />
        <Line label="Line status" value={words(d.lineStatus)} />
        <div style={{ ...S.cardTitle, marginTop: '12px' }}>Patient's concern</div>
        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{d.concernReason || '(none recorded)'}</div>
        {d.requestId && <button type="button" style={{ ...S.btn, marginTop: '16px' }} onClick={() => onViewRequest(d.requestId)}>View request</button>}
      </div>}
    </div>
  );
}
